import {
  enableProdMode,
  LOCALE_ID,
  ErrorHandler,
  provideAppInitializer,
  inject,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { registerLocaleData } from '@angular/common';
import * as de from '@angular/common/locales/de';

import { Environment, ServiceType } from '@nimmsta/ng-commons-lib/config';
import { NAV_ITEM_SERVICE } from '@nimmsta/ng-commons-lib/navigation';
import { NimmstaCommonsModule } from '@nimmsta/ng-commons-lib/setup';
import { initSentry } from '@nimmsta/ng-commons-lib/utils/sentry';
import { initQuill } from '@nimmsta/ng-commons-lib/editor';
import { LanguageService } from '@nimmsta/ng-commons-lib/services/language';
import {
  provideKeycloakInitializer,
  provideKeycloakBearerInterceptorConfig,
} from '@nimmsta/ng-commons-lib/auth';
import {
  provideRouter,
  Router,
  withDisabledInitialNavigation,
} from '@angular/router';
import * as Sentry from '@sentry/angular';
import { DatePipe, NgOptimizedImage } from '@angular/common';
import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { routes } from './app/app.routes';
import { MatDividerModule } from '@angular/material/divider';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MarkdownModule } from 'ngx-markdown';
import { FormsModule } from '@angular/forms';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatInputModule } from '@angular/material/input';
import { AccordionModule } from 'primeng/accordion';
import { MultiSelectModule } from 'primeng/multiselect';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import {
  AutoRefreshTokenService,
  includeBearerTokenInterceptor,
  provideKeycloak,
  UserActivityService,
  withAutoRefreshToken,
} from 'keycloak-angular';
import { MatTreeModule } from '@angular/material/tree';
import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';
import { ProductFruitsService } from './app/services/productfruits.service';
import { WorkflowNavItemService } from './app/services/workflownav.service';
import { VERSION } from './environments/version';
import WebFont from 'webfontloader';

if (environment.production) {
  enableProdMode();
}

// Register locale data for German language support
registerLocaleData(de.default);

// Load custom web fonts
WebFont.load({
  custom: { families: ['Material Icons', 'Material Symbols Outlined'] },
});

// Initialize Quill editor
initQuill();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(self as any).MonacoEnvironment = {
  getWorkerUrl: function () {
    return 'assets/monaco/vs/base/worker/workerMain.js';
  },
};

// Initialize Sentry for error tracking and performance monitoring
// This should be done before bootstrapping the Angular application
switch (environment.environment) {
  case Environment.PROD:
  case Environment.STAGING:
  case Environment.DEV:
    initSentry({
      dsn: 'https://49eccb54d299cda1c1debb4ab18993b1@sentry.nimmsta.com/6',
      release: VERSION.fullHash,
      environment,
      sendDefaultPii: true,
    });
    break;
  case Environment.LOCAL:
    // Local development environment, no Sentry initialization
    break;
}

// Bootstrap the Angular application
bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    provideRouter(routes, withDisabledInitialNavigation()),
    importProvidersFrom(
      MatDividerModule,
      BrowserModule,
      MatProgressSpinnerModule,
      MarkdownModule.forRoot(),
      FormsModule,
      MatPaginatorModule,
      MatInputModule,
      NgOptimizedImage,
      AccordionModule,
      MultiSelectModule,
      MonacoEditorModule.forRoot({
        baseUrl: 'assets/monaco/vs',
        defaultOptions: {
          scrollBeyondLastLine: false,
          theme: 'vs-dark',
          automaticLayout: true,
          wordWrap: 'on',
          tabSize: 2,
        },
      }),
      MatTreeModule,
      NimmstaCommonsModule.forRoot({
        environment: environment.environment as Environment | undefined,
        serviceType: ServiceType.CLOUD,
        apiUrls: environment.apiUrls,
        applicationUrls: environment.applicationUrls,
        defaultPageSizeOptions: environment.defaultPageSizeOptions,
        defaultPageSize: environment.defaultPageSize,
        USER_KEY: environment.USER_KEY,
        keycloakUrl: environment.keycloakUrl,
        keycloakRealm: environment.keycloakRealm,
        keycloakClientId: environment.keycloakClientId,
        defaultConfigurationVersion: environment.defaultConfigurationVersion,
        androidAppSoftwareId: environment.androidAppSoftwareId,
        translationFiles: [
          './assets/i18n/commons/',
          './assets/i18n/b2b/',
          './assets/i18n/workflow/',
          './assets/i18n/',
        ],

        maintenanceInfoSource: 'assets/maintenance/cloud-maintenance.json',
      }),
    ),
    { provide: LOCALE_ID, useValue: 'de' },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideAppInitializer(() => inject(LanguageService).initializeLanguage()),
    provideAppInitializer(() => {
      const initializerFn = (
        (service: ProductFruitsService) => () =>
          service.serviceInit()
      )(inject(ProductFruitsService));
      return initializerFn();
    }),
    DatePipe,
    {
      provide: NAV_ITEM_SERVICE,
      useClass: WorkflowNavItemService,
    },
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([includeBearerTokenInterceptor]),
    ),
    provideAnimations(),

    /**
     * Keycloak configuration
     * These providers should be called as last in chain
     */
    provideKeycloak({
      config: {
        url: environment.keycloakUrl,
        realm: environment.keycloakRealm,
        clientId: environment.keycloakClientId,
      },
      features: [
        withAutoRefreshToken({
          onInactivityTimeout: 'logout',
        }),
      ],
      providers: [AutoRefreshTokenService, UserActivityService],
    }),
    provideKeycloakBearerInterceptorConfig(environment),
    provideKeycloakInitializer(),
  ],
});
